<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('users.usersLabel') }}</span>
        <h3 class="page-title">{{ $t('users.editUser') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/uzytkownicy')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button theme="success" @click.prevent="validateForm">{{ $t('ui.saveChanges') }}</d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="user-edit-form" tag="form" @keydown.enter.prevent>
              <div class="form-group">
                <label for="name">
                  {{ $t('ui.nameAndSurename') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('ui.eMailCap') }}:
                </label>

                <validation-provider
                  rules="required|email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="email"
                    v-model="email" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('login.password') }}:
                </label>

                <validation-provider
                  rules="min:6"
                  name="password"
                  v-slot="{ errors }">
                  <d-input
                    type="password"
                    :state="errors.length ? 'invalid' : null"
                    v-model="password" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">
                  {{ $t('ui.repeatPassword') }}:
                </label>

                <validation-provider
                  rules="min:6"
                  name="password-confirmation"
                  v-slot="{ errors }">
                  <d-input
                    type="password"
                    :state="errors.length ? 'invalid' : null"
                    v-model="passwordConfirmation" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('ui.language') }}:
                </label>

                <div :class="{
                  'form-control': true,
                  'is-select': true
                }">
                  <v-select
                    :options="languageOptions"
                    v-model="language_id"
                    :searchable="false"
                    :clearable="false"
                    :reduce="language => language.value"
                    :placeholder="$t('settings.language.chooseLanguage')"
                    label="text">
                    <template slot="no-options">
                      {{ $t('settings.language.noLanguagesAvailable') }}&hellip;
                    </template>
                  </v-select>
                </div>
              </div>

              <div class="form-group">
                <label for="contacts">
                  {{ $t('ui.selectContactPerson') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="partner_contact_id"
                  v-slot="{ errors }">
                  <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                    <v-select
                      :options="contacts"
                      v-model="partner_contact_id"
                      :reduce="contact => contact.id"
                      label="name"
                      :placeholder="$t('welcome.choose')">
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.name }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.name }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <d-checkbox inline v-model="active" toggle>
                  {{ $t('users.isAccountActive') }}
                </d-checkbox>
              </div>
            </validation-observer>

            <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'user-edit',
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedContacts && this.loadedLanguages;
    },
    languageOptions () {
      return this.languages.map(language => ({
        value: language.id,
        text: language.name + ' (' + language.code + ')'
      }));
    }
  },
  data () {
    return {
      contacts: [],
      languages: [],
      id: 0,
      name: '',
      email: '',
      language_id: null,
      partner_contact_id: null,
      active: false,
      password: '',
      passwordConfirmation: '',
      // flags
      loadedContacts: false,
      loadedLanguages: false,
      loaded: false,
      loadError: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      endpointBase: '/customer-panel/users/item/',
      instanceFields: ['id', 'name', 'email', 'active', 'language_id', 'partner_contact_id'],
      booleanFields: ['active'],
      loadedKey: 'loaded',
      successAction: () => {
        this.loadAdditionalData();
      }
    });
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/customers/contacts/items',
        loadedKey: 'loadedContacts',
        outputKey: 'contacts',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError',
        successAction: () => {
          this.languages.reverse();

          if (this.language_id === null) {
            this.language_id = this.languages[0].id;
          }
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['user-edit-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        name: this.name,
        email: this.email,
        active: this.active ? 1 : 0,
        language_id: this.language_id,
        partner_contact_id: this.partner_contact_id,
      };

      if (this.password !== '' && this.passwordConfirmation !== '') {
        formDataToSend.password = this.password;
        formDataToSend.password_confirmation = this.passwordConfirmation;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customer-panel/users/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('users.editUserSuccessTitle'),
        successText: this.$t('users.editUserSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('users.editUserErrorText'),
        successAction: () => {
          if (
            this.id === this.$store.getters.getUserID &&
            this.$store.getters.getUserLang !== parseInt(this.language_id, 10)
          ) {
            window.localStorage.setItem('headwear-crm-ui-language', this.language_id);
            window.location.reload();
          } else {
            this.$router.push('/uzytkownicy');
          }
        }
      });
    }
  }
};
</script>
